import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, CheckCircle2 } from 'lucide-react';
import LandingNavbar from '../components/landing/LandingNavbar';
import LandingFooter from '../components/landing/LandingFooter';
import { toast } from '../lib/toast';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '../styles/publicTheme.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactUsPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = usePublicTheme();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await axios.post(`${API}/platform/contact`, form);
      setSent(true);
      toast.success('تم إرسال رسالتك بنجاح');
    } catch (err) {
      setError(err?.response?.data?.detail || 'فشل إرسال الرسالة، حاول مرة أخرى');
    }
    setLoading(false);
  };

  const inputCls = "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-slate-500 outline-none transition-colors focus:border-teal-500/60 focus:bg-white/[0.05]";

  return (
    <div className="min-h-screen bg-[#060f1c] text-white" dir="rtl" data-testid="contact-us-page" data-pub-theme={theme}>
      <LandingNavbar platformSettings={{}} scrolled navigate={navigate} theme={theme} onToggleTheme={toggleTheme} />
      <div className="mx-auto max-w-lg px-5 pb-16 pt-32">
        <div className="mb-6 flex items-center justify-center gap-2">
          <Mail className="h-6 w-6 text-teal-400" />
          <h1 className="font-display text-2xl font-bold text-white">تواصل معنا</h1>
        </div>
        <p className="mb-8 text-center text-sm text-slate-400">لديك استفسار أو ملاحظة؟ راسلنا وسنعود إليك في أقرب وقت</p>

        {sent ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-teal-500/30 bg-teal-500/5 p-8 text-center" data-testid="contact-us-success">
            <CheckCircle2 className="h-10 w-10 text-teal-400" />
            <p className="font-semibold text-white">تم إرسال رسالتك بنجاح</p>
            <p className="text-sm text-slate-400">سيتم التواصل معك قريباً</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-md" data-testid="contact-us-form">
            {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="contact-us-error">{error}</div>}
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-300">الاسم</label>
              <input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-us-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-300">البريد الإلكتروني</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-us-email-input" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-300">رقم الجوال</label>
                <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-us-phone-input" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-300">الموضوع</label>
              <input required className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-us-subject-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-300">الرسالة</label>
              <textarea required rows={5} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-us-message-input" />
            </div>
            <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-l from-orange-500 to-orange-400 py-3 text-sm font-bold text-white shadow-[0_0_15px_rgba(245,76,7,0.4)] transition-all hover:shadow-[0_0_22px_rgba(245,76,7,0.6)] disabled:opacity-50" data-testid="contact-us-submit-btn">
              <Send className="h-4 w-4" />
              {loading ? 'جارِ الإرسال...' : 'إرسال الرسالة'}
            </button>
          </form>
        )}
      </div>
      <LandingFooter platformSettings={{}} landing={null} />
    </div>
  );
}
